import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { hitTarget, searchQueryOptions } from '@/entities/search'
import { cn, useDebouncedValue } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/icon'
import { HitRow } from './hit-row'

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: '/meetings', label: 'Встречи', icon: 'camera' },
]

const SEARCH_DEBOUNCE_MS = 250

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function SectionTitle({ children }: { children: string }) {
  return <p className="label px-3 pt-3 pb-1">{children}</p>
}

function CommandRow({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean
  icon: IconName
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-control px-3 py-2 text-left text-sm transition-colors',
        active ? 'bg-bg-elevated text-text-primary' : 'text-text-secondary hover:bg-bg-elevated',
      )}
    >
      <Icon name={icon} size={16} className="shrink-0 text-text-tertiary" />
      <span className="truncate">{label}</span>
    </button>
  )
}

// Глобальная ⌘K-палитра: универсальный поиск (встречи, расшифровки, компании,
// проекты, теги — GET /api/search) + быстрая навигация. Монтируется в
// pages/__root рядом с AppShell (page→widgets). Открытие — проп + глобальный ⌘K.
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounced = useDebouncedValue(query, SEARCH_DEBOUNCE_MS)
  const trimmed = debounced.trim()
  const search = useQuery(searchQueryOptions(trimmed, { limit: 20 }))

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  // autoFocus запрещён линтером → ref + эффект на открытие.
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  if (!open) return null

  const close = () => {
    onOpenChange(false)
    setQuery('')
    setActive(0)
  }

  const q = query.trim()
  // Палитре хватает страницы выдачи; полная лента встреч — на /meetings?q=.
  const hits = trimmed.length > 0 ? (search.data ?? []) : []
  const navMatches = NAV.filter((n) => q === '' || n.label.toLowerCase().includes(q.toLowerCase()))
  const searching = trimmed.length > 0 && search.isFetching
  const noHits = trimmed.length > 0 && !search.isFetching && hits.length === 0

  const go = (to: string) => {
    void navigate({ to })
    close()
  }
  // Плоский список активируемых по Enter действий, в порядке отрисовки.
  // У компаний/проектов/тегов своих страниц нет — hitTarget уводит в список
  // встреч с проставленным фильтром.
  const activations = [
    ...hits.map((h) => () => {
      void navigate(hitTarget(h))
      close()
    }),
    ...navMatches.map((n) => () => go(n.to)),
  ]
  const total = activations.length
  const activeIdx = Math.min(active, Math.max(0, total - 1))
  const navStart = hits.length

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, total - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      activations[activeIdx]?.()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-center px-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Закрыть поиск"
        onClick={close}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative z-10 h-fit w-full max-w-xl overflow-hidden rounded-card border border-border-strong bg-bg-surface shadow-lg">
        <div className="relative flex items-center gap-3 border-b border-border-default px-4">
          <Icon name="search" size={18} className="shrink-0 text-text-tertiary" />
          <input
            ref={inputRef}
            type="text"
            aria-label="Поиск по встречам, справочникам и командам"
            value={query}
            placeholder="Встречи, компании, проекты, теги, сказанное…"
            onChange={(e) => {
              setQuery(e.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
            className="w-full bg-transparent py-4 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
          {searching && (
            <span
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-0.5 animate-pulse rounded-pill bg-border-accent"
            />
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {q.length > 0 && (
            <section aria-label="Результаты поиска">
              <SectionTitle>Найдено</SectionTitle>
              {noHits ? (
                <p className="px-3 py-2 text-sm text-text-tertiary">
                  Ничего не нашлось. Проверьте запрос.
                </p>
              ) : (
                hits.map((h, i) => (
                  <HitRow
                    key={`${h.kind}:${h.entityId}`}
                    hit={h}
                    active={activeIdx === i}
                    onSelect={close}
                  />
                ))
              )}
            </section>
          )}

          {navMatches.length > 0 && (
            <section aria-label="Перейти">
              <SectionTitle>Перейти</SectionTitle>
              {navMatches.map((n, i) => (
                <CommandRow
                  key={n.to}
                  active={activeIdx === navStart + i}
                  icon={n.icon}
                  label={n.label}
                  onClick={() => go(n.to)}
                />
              ))}
            </section>
          )}

          {q.length === 0 && (
            <p className="px-3 pt-2 pb-1 text-xs text-text-tertiary">
              Поиск по названию, тегам и сказанному на встречах.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
